import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Authority, UserRole, fireauth } from "../auth/AuthSlice"
import { Page, Pageable, defaultPageContent } from "../admin/AdminSlice"
import { RootState } from "../../app/store"
import axios from "axios"
import { API_BASE } from "../../app/const"

export interface UserSubscriptionTransferReq {
  subscriptionId: number
  targetUserId: number
}

export interface UserSubscription {
  id: number
  stripeId: string
  userId: string
  userEmail: string
  amountTotal: number
  currency: string
  invoice: string
  eventTime: number
  stripeEmail: string
  stripeName: string
  stripePhone: string
  stripeMode: string
  paymentStatus: string
  status: string
  subscription: string
  subscriptionDurationMode: string // 'monthly' | 'yearly'
  ackId: string
  adminReviewed: boolean
  adminComments: string
  paidTierCount: number
}

export interface AdminStripeState {
  status: 'idle' | 'loading' | 'failed'
  adminStripe: UserSubscription
  adminStripes: Page<UserSubscription>
  searchUsers: Page<UserRole>
  autoAddResult?: {
    success: UserRole[],
    failure: UserRole[]
  }
  selectedUser: UserRole
}

const initialState: AdminStripeState = {
  status: 'idle',
  adminStripe: {
    id: 0,
    stripeId: '',
    userId: '',
    userEmail: '',
    amountTotal: 0,
    currency: '',
    invoice: '',
    eventTime: 0,
    stripeEmail: '',
    stripeName: '',
    stripePhone: '',
    stripeMode: '',
    paymentStatus: '',
    status: '',
    subscription: '',
    subscriptionDurationMode: '',
    ackId: '',
    adminReviewed: false,
    adminComments: '',
    paidTierCount: 0
  },
  adminStripes: defaultPageContent,
  searchUsers: {
    content: [],
    empty: false,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    pageable: {
      offset: 0,
      pageNumber: 0,
      pageSize: 0,
      paged: false,
      sort: {
        empty: false,
        sorted: false,
        unsorted: false
      },
      unpaged: false
    },
    size: 0,
    sort: {
      empty: false,
      sorted: false,
      unsorted: false
    },
    totalElements: 0,
    totalPages: 0
  },
  selectedUser: {
    id: 0,
    userId: '',
    paidTier: 0,
    paidTierCount: 0,
    paidTierExpiry: 0,
    org: {
      id: 0,
      name: '',
      ownerId: '', 
      ownerEmail: '', 
      paidTier: 0, 
      paidTierCount: 0, 
      paidTierExpiry: 0
    }
  }
}
const isAdmin = (userAuthorities: [Authority] | undefined) => {
  if (userAuthorities) {
    return userAuthorities.some(authority => authority.authority === 'ROLE_ADMIN')
  }
  return false
}

export const fetchAllAdminStripeAsync = createAsyncThunk(
  'account/fetchAllAdminStripeAsync', async ({ mode, userAuthorities }: { mode: string, userAuthorities: [Authority] | undefined }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/user/subscription/${userAuthorities && userAuthorities.some(e => e.authority === 'ROLE_ADMIN') ? 'all' : 'my'}?size=500`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const transferStripeSubscripitionAsync = createAsyncThunk(
  'admin/transferStripeSubscripitionAsync', async ({ transferReq }: { transferReq: UserSubscriptionTransferReq }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const headers = {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      }
      const response = await axios.post(`${API_BASE}apps/user/subscription/transfer`, transferReq, headers)
      return response.data
    }
  }
)

export const markAsReviewed = createAsyncThunk('admin/markAsReviewed', async ({ subscriptionId, adminComments, adminReviewed }: { subscriptionId: string, adminComments: string, adminReviewed: boolean }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.put(`${API_BASE}apps/user/subscription/${subscriptionId}`, { adminComments, adminReviewed }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})


export const searchUser = createAsyncThunk('admin/searchUser', async ({ query }: { query: string }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.get(`${API_BASE}apps/user/search/${query}`, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return response.data
  }
})


export const adminStripeSlice = createSlice({
  name: 'admin-stripe',
  initialState,
  reducers: {
    resetAdminStripe: (state) => {
      state.status = 'idle'
      state.adminStripe = initialState.adminStripe
      state.adminStripes = initialState.adminStripes
      state.searchUsers = initialState.searchUsers
      state.selectedUser = initialState.selectedUser
    },
    resetAdminStripeSearchUser: (state) => {
      state.status = 'idle'
      state.searchUsers = initialState.searchUsers
      state.selectedUser = initialState.selectedUser
    },
    pickAdminStripe: (state, action) => {
      state.status = 'idle'
      state.adminStripe = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchAllAdminStripeAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllAdminStripeAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.adminStripe = initialState.adminStripe
        state.adminStripes = action.payload
      }).addCase(fetchAllAdminStripeAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.adminStripe = initialState.adminStripe
        state.adminStripes = initialState.adminStripes
      }).addCase(transferStripeSubscripitionAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(transferStripeSubscripitionAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.adminStripe = action.payload
      }).addCase(transferStripeSubscripitionAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.adminStripe = initialState.adminStripe
      }).addCase(markAsReviewed.pending, (state) => {
        state.status = 'loading'
      }).addCase(markAsReviewed.fulfilled, (state, action) => {
        state.status = 'idle'
        state.searchUsers = action.payload
      }).addCase(markAsReviewed.rejected, (state, action) => {
        state.status = 'failed'
        state.searchUsers = initialState.searchUsers
      }).addCase(searchUser.pending, (state) => {
        state.status = 'loading'
      }).addCase(searchUser.fulfilled, (state, action) => {
        state.status = 'idle'
        state.searchUsers = action.payload
        state.selectedUser = action.payload.content[0]
      }).addCase(searchUser.rejected, (state, action) => {
        state.status = 'failed'
        state.autoAddResult = undefined
      })
  }
})

export const { resetAdminStripe, resetAdminStripeSearchUser, pickAdminStripe } = adminStripeSlice.actions
export const selectAdminStripes = (state: RootState) => state.adminStripe.adminStripes
export const selectAdminStripe = (state: RootState) => state.adminStripe.adminStripe
export const selectSearchedUsers = (state: RootState) => state.adminStripe.searchUsers
export const selectSelectedUser = (state: RootState) => state.adminStripe.selectedUser
export const selectAdminStripeStatus = (state: RootState) => state.adminStripe.status
export const selectAutoAddResult = (state: RootState) => state.adminStripe.autoAddResult
export default adminStripeSlice.reducer